import { useEffect } from 'react';

// iOS PWA standalone: 100dvh doesn't always match the real viewport (a
// WebKit quirk that varies by iOS version) — the mismatch leaves a sliver
// of the WKWebView's own white background showing through at the top/bottom
// safe areas that html/body's gradient is supposed to cover. Measure the
// real height in JS and drive html/body's height from that custom property
// instead; 100dvh stays as the fallback for the first paint, before this
// effect has run.
//
// Segundo problema, medido en un iPhone 16 Pro Max con iOS 27 (2026-09-25):
// en modo standalone con `black-translucent` la ventana de la PWA mide 894pt
// de una pantalla de 956 — termina 62pt antes del borde físico, sin importar
// la meta viewport (se probaron cuatro variantes) — y
// `env(safe-area-inset-bottom)` sigue diciendo 34. La barra de inicio ya quedó
// afuera de la ventana: restarle además la safe area contaba ese borde dos
// veces. Se descuenta sólo la parte de la safe area que cae adentro de la
// ventana (`env() - hueco`). Ver `bottomGap` para cuándo el hueco está abajo y
// cuándo arriba (barra de estado opaca, que es lo que usa index.html ahora).
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Cuánto le falta a la ventana para llegar al borde físico de abajo.
 *
 * Con `black-translucent` la ventana se sube debajo de la barra de estado y
 * termina antes de abajo: el hueco es del tamaño de la safe area de arriba
 * (62pt = `env(safe-area-inset-top)`). Con la barra opaca (`default`) la
 * ventana empieza debajo de la barra y llega hasta abajo: `env(top)` es 0 y el
 * hueco de 62pt está ARRIBA, no abajo. Por eso el hueco de abajo nunca puede
 * ser más grande que la safe area de arriba: restarlo entero dejaba la app
 * pegada a la barra de inicio cuando el hueco estaba del otro lado.
 */
function bottomGap(insetTop: number): number {
  if (!isStandalone()) return 0; // en Safari el resto es la barra del navegador, no un hueco
  const vv = window.visualViewport;
  const bottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
  // iOS no rota `screen`: la altura de pantalla es el lado largo en vertical y el corto acostado.
  const portrait = window.innerHeight >= window.innerWidth;
  const long = Math.max(screen.width, screen.height);
  const short = Math.min(screen.width, screen.height);
  const gap = Math.max(0, Math.round((portrait ? long : short) - bottom));
  return Math.min(gap, Math.round(insetTop));
}

export function useViewportHeightFix(): void {
  useEffect(() => {
    const root = document.documentElement;

    // `env()` no se lee como número: un elemento de prueba que lo usa de padding.
    const insetProbe = document.createElement('div');
    insetProbe.setAttribute('aria-hidden', 'true');
    insetProbe.style.cssText = 'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px)';
    document.body.appendChild(insetProbe);

    const setVh = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      root.style.setProperty('--app-vh', `${h}px`);

      const gap = bottomGap(parseFloat(getComputedStyle(insetProbe).paddingTop) || 0);
      if (gap > 0) {
        root.style.setProperty('--sa-bottom', `max(0px, calc(env(safe-area-inset-bottom, 0px) - ${gap}px))`);
      } else {
        root.style.removeProperty('--sa-bottom'); // vuelve al env() de tokens.css
      }
    };

    setVh();

    const onVisible = () => {
      if (document.visibilityState === 'visible') setVh();
    };

    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    window.visualViewport?.addEventListener('resize', setVh);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
      window.visualViewport?.removeEventListener('resize', setVh);
      document.removeEventListener('visibilitychange', onVisible);
      root.style.removeProperty('--sa-bottom');
      insetProbe.remove();
    };
  }, []);
}
